import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Fieldset from '../Fieldset';

export default class RadioGroupWithValidation extends React.PureComponent {
    static propTypes = {
        /** Expected to be one or more Radios */
        children: PropTypes.node.isRequired,

        /** Id given to outer container and to link group and error message */
        id: PropTypes.string.isRequired,

        /** The name given to each Radio */
        name: PropTypes.string.isRequired,

        /** Label spoken to screen readers. */
        label: PropTypes.node.isRequired,

        /** Makes a visual-only label with the contents of label */
        isLabelVisible: PropTypes.bool,

        /** The value of the selected item in the group */
        value: PropTypes.string,

        /** Change handler passed to each radio */
        onChange: PropTypes.func,

        /** Makes the group required */
        isRequired: PropTypes.bool,

        /** Makes all the Radios in the group disabled */
        isDisabled: PropTypes.bool,

        /** Contents of the error message, usually text */
        errorText: PropTypes.node,

        /** Marks and styles the wrapper as invalid */
        isInvalid: PropTypes.bool,

        /** A function that accepts the field's value and returns an object with
        specific validation-related keys */
        validator: PropTypes.func,

        /** Makes the radio inputs arranged inline horizontally */
        isInline: PropTypes.bool,

        /** additional class applied to outer container */
        className: PropTypes.string
    };

    static defaultProps = {
        value: undefined,
        onChange: undefined,
        isRequired: false,
        isDisabled: false
    };

    constructor(props) {
        super(props);

        this.state = {
            isInvalid: props.isInvalid,
            errorText: props.errorText
        };

        this.wrapperRef = React.createRef();
    }

    componentDidUpdate(prevProps) {
        const { isInvalid, errorText, value } = this.props;

        if (isInvalid !== prevProps.isInvalid) {
            this.setState({ isInvalid });
        }

        if (errorText !== prevProps.errorText) {
            this.setState({ errorText });
        }

        if (value !== prevProps.value && this.state.isInvalid) {
            this.validate();
        }
    }

    validate = () => {
        const { value, validator, isRequired, id, name, label } = this.props;
        const radios = document.getElementsByName(name);
        let currentValue = value;
        const validValues = [];

        for (let i = 0; i < radios.length; i += 1) {
            validValues.push(radios[i].value);

            if (!value && radios[i].checked) {
                currentValue = radios[i].value;
            }
        }

        const validationObject = {
            isInvalid: false,
            errorText: null,
            id
        };

        if (isRequired && (!currentValue || !validValues.includes(currentValue))) {
            const isRequiredErrorText = `${label} is required`;
            validationObject.isInvalid = true;
            validationObject.errorText = isRequiredErrorText;

            this.setState({
                isInvalid: true,
                errorText: isRequiredErrorText
            });
        }
        else if (value && validator) {
            const parsed = validator(currentValue);

            // Value passes validation
            if (parsed.isValid) {
                validationObject.isInvalid = false;
                validationObject.errorText = null;
            }
            else {
                validationObject.isInvalid = true;
                validationObject.errorText = parsed.errorText || null;
            }
        }
        else {
            this.setState({
                isInvalid: false,
                errorText: null
            })
        }

        return validationObject;
    }

    render() {
        const {
            children,
            value,
            name,
            isRequired,
            isDisabled,
            onChange,
            className,
            id,
            label,
            isLabelVisible,
            isInline
        } = this.props;

        const {
            isInvalid,
            errorText
        } = this.state;

        return (
            <Fieldset
                id={id}
                label={label}
                isLabelVisible={isLabelVisible}
                isInvalid={isInvalid}
                errorText={errorText}
                isRequired={isRequired}
                className={className}>
                <div className={cx({
                    'sarsa-radio-group-inline': isInline
                })}>
                    {React.Children.map(children, (child) => {
                        return React.cloneElement(child, {
                            isChecked: onChange ? value === child.props.value : undefined,
                            name,
                            isRequired,
                            isDisabled: isDisabled || child.props.isDisabled,
                            onChange
                        })
                    })}
                </div>
            </Fieldset>
        );
    }
}
